<template>
  <div class="container py-5">

    <div class="level">
      <div class="level-left buttons flex-row mb-0">
        <div class="title is-4 mr-3 ml-1">
          Drawing Highlight Requests
        </div>
      </div>

      <div class="level-right buttons">
        <v-fade-transition>
          <button 
            class="button"
            v-if="selected.length > 0"
            @click="launchConfirmDelete()">
            <span class="icon is-medium">
              <i class="fas fa-trash has-text-danger"></i>
            </span>
            <span>Delete {{ selected.length }} selected drawing highlights</span>
          </button>
        </v-fade-transition>
        <search-autocomplete
          :value="filters.project_id"
          @input="setFilter({ field: 'project_id', value: $event })"
          :product-brand="filters.product_brand">
        </search-autocomplete>
      </div>
    </div>

    <div class="tabs mb-0 mt-2">
      <ul>
        <li 
          @click="setFilter({ field: 'product_brand', value: 'Mitrex' })"
          :class="{ 'is-active': filters.product_brand == 'Mitrex' }">
          <a>Mitrex</a>
        </li>
        <li 
          @click="setFilter({ field: 'product_brand', value: 'Cladify' })"
          :class="{ 'is-active': filters.product_brand == 'Cladify' }">
          <a>Cladify</a>
        </li>
      </ul>
    </div>

    <v-fade-transition mode="out-in">

      <v-progress
        v-if="loading"
        :value="true">
      </v-progress>

      <div class="mt-3" v-else>

        <article 
          v-if="!loading && drawingHighlights.length == 0" 
          class="message is-info is-fullwidth mt-3">
          <div class="message-body">
            There are no Drawing Highlights.
          </div>
        </article>

        <table 
          v-if="drawingHighlights.length > 0"
          class="table is-hoverable is-clickable 
            is-fullwidth has-small-font sticky-header">
          <thead>
            <tr>
              <th>
                <v-checkbox 
                  :value="allSelected" 
                  @input="toggleAllSelected($event)"/>
              </th>
              <th 
                v-for="(header, headerIndex) in headers" 
                :key="headerIndex">
                {{ header.label }}
              </th>
              <th></th>
            </tr>
          </thead>

          <tbody v-if="drawingHighlights.length > 0">
            <tr 
              class="v-centered"
              v-for="highlight in drawingHighlights"
              :key="highlight.id"
              @click="viewDrawingHighlight(highlight)">
              <td>
                <v-checkbox 
                  @click.native.stop=""
                  :value="selected.includes(highlight.id)" 
                  @input="toggleSelected(highlight.id)"/>
              </td>
              <td>
                {{ highlight.project_rfq ? highlight.project_rfq.project.project_name : '' }}
              </td>
              <td>
                <a 
                  v-if="highlight.project_rfq" 
                  :href="highlight.project_rfq.attachment" 
                  target="_blank">
                  {{ highlight.project_rfq.rfq_name }}
                </a>
              </td>
              <td @click.stop="">
                <a 
                  v-if="highlight.project_rfq && highlight.project_rfq.drawing" 
                  :href="highlight.project_rfq.drawing" 
                  target="_blank">
                  Link to Drawings
                </a>
              </td>
              <td class="is-capitalized">
                {{ highlight.status.toLowerCase() }}
              </td>
              <td>
                <span class="is-size-7">
                  {{ formatDate(parseDate(highlight.created_on)) }}
                </span>
              </td>
              <td>
                <div class="buttons buttons-right">
                  <button
                    @click.stop="launchConfirmDelete(highlight.id)" 
                    class="button is-small">
                    <span class="icon">
                      <i class="fas fa-trash has-text-grey"></i>
                    </span>
                    <span>Delete</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

        </table>
      </div>
    </v-fade-transition>

    <v-pagination 
      class="mt-3"
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VProgress from '@/components/base/VProgress'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'

import { parseJSON, format } from 'date-fns'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VConfirmModal, 
    VPagination, 
    VProgress,
    VCheckbox, 
    VFadeTransition,
    SearchAutocomplete
  },
  data() {
    return {
      headers: [
        { label: 'Project' },
        { label: 'RFQ Name' },
        { label: 'Drawings' },
        { label: 'Status' },
        { label: 'Created On' }
      ],
      adding: false,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this drawing highlight?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      parseDate: (date) => { return date ? parseJSON(date) : date },
      formatDate: (date) => date ? format(date, "yyyy-MM-dd HH:mm:ss") : date
    }
  },
  computed: {
    ...mapState("drawingHighlights", [
      "drawingHighlights", "loading", "pagination", 
      "allSelected", "selected", "filters"
    ])
  },
  watch: {
    'pagination.page'() {
      let params = { ...this.filters, ...this.pagination }
      this.getDrawingHighlights(params);
    },
    filters: {
      handler(filters) {
        this.getDrawingHighlights(filters);
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("drawingHighlights", [
      "getDrawingHighlights", "deleteDrawingHighlight", 
      "toggleAllSelected"
    ]),
    ...mapMutations("drawingHighlights", [
      "changePage", "toggleSelected", "setFilter"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      this.getDrawingHighlights();
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteDrawingHighlight(id);
            this.confirmDelete.state = false;
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete \
          the ${this.selected.length} selected drawing highlights?`;
        this.confirmDelete.state = true;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteDrawingHighlight(id);
          this.confirmDelete.state = false;
        }
        this.confirmDelete.message = 'Are you sure you want to delete \
          this drawing highlight?';
        this.confirmDelete.state = true; 
      }
    },
    viewDrawingHighlight(highlight) {
      this.$router.push({
        name: 'DrawingHighlightViewer', 
        params: {
          drawingHighlightId: highlight.id,
          drawingHighlight: highlight
        } 
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>